import React from 'react';
import UpgradeProgressStep from './UpgradeProgressStep';
import { estimateCurrentYear, estimateProcessedCount, progressEstimates } from './progressEstimates';
import { ProgressData } from './types';
import useEstimatedProgress from './useEstimatedProgress';

const steps = [
  {
    id: 'migration',
    pending: 'Migrate transactions',
    active: 'Migrating transactions',
    completed: 'Transactions migrated',
  },
  {
    id: 'recalculation',
    pending: 'Recalculate cost basis',
    active: 'Recalculating cost basis',
    completed: 'Cost basis recalculated',
  },
  {
    id: 'finalization',
    pending: 'Finalize upgrade',
    active: 'Finalizing upgrade',
    completed: 'Upgrade finalized',
  },
];

export default function UpgradeProgress({ currentStep, transactionCount, taxYearCount }: ProgressData) {
  const estimate = progressEstimates[currentStep];
  const progress = useEstimatedProgress(estimate.durationMs, true);
  const activeIndex = steps.findIndex(({ id }) => id === currentStep);
  return (
    <ol aria-label="Upgrade progress" className="space-y-4">
      {steps.map((step, index) => {
        const status = index < activeIndex ? 'completed' : index === activeIndex ? 'active' : 'pending';
        let label = step[status];
        if (step.id === 'migration' && transactionCount !== undefined) {
          const total = transactionCount.toLocaleString('en-US');
          if (status === 'active' && transactionCount > 0) {
            label = `Migrating ${estimateProcessedCount(transactionCount, progress).toLocaleString(
              'en-US',
            )} of ${total} transactions`;
          } else if (status === 'completed') {
            label = `${total} ${transactionCount === 1 ? 'transaction' : 'transactions'} migrated`;
          }
        }
        if (step.id === 'recalculation') {
          if (status === 'active' && taxYearCount > 0) {
            label = `Recalculating cost basis of year ${estimateCurrentYear(
              taxYearCount,
              progress,
            )} of ${taxYearCount}`;
          } else if (status === 'completed') {
            label = `Cost basis recalculated across ${taxYearCount} tax ${
              taxYearCount === 1 ? 'year' : 'years'
            }`;
          }
        }
        return (
          <UpgradeProgressStep
            key={step.id}
            label={label}
            status={status}
            number={index + 1}
            progress={status === 'active' ? progress : undefined}
            durationLabel={status === 'active' ? estimate.label : undefined}
          />
        );
      })}
    </ol>
  );
}
